import {
    getfromDataObject,
    validateStringData,
} from 'utils/formatting';
import { portfolioTypeIds } from 'constants/portfolioTypeIds';
import {
    portfolioStatuses,
    portfolioStatusesColors,
    IN_EXECUTION, NOT_IMPLEMENTED,
    BLOCKED, CLOSED, getPortfolioStatus,
    PENDING_ORDERS,
} from 'constants/portfolioStatuses';
import { Portfolio } from 'core/types/api/wms';

// --- Interfaces ---

interface PortfolioOverviewItem {
    Id: string | number;
    HasPendingOrders?: boolean;
}

interface AdaptPortfoliosParams {
    data: { Portfolios?: Portfolio[] };
    dfsId: string | number;
    portfolioActions: (params: any) => any[]; // Replace 'any' with your specific Action type if available
    t: (key: string) => string;
    getFormattedCurrency: (value: number, options: { currency: string }) => string;
    portfoliosOverview?: PortfolioOverviewItem[];
}

interface AdaptedPortfolio {
    UId: string;
    Name: {
        label: string;
        link: string;
        disabled: boolean;
    };
    Product: string;
    Profile: string;
    Status: {
        text: string;
        value: string;
        color: string;
    };
    ExternalId?: string;
    Value: string;
    Performance?: number;
    Actions: {
        value: string;
        actions: any[];
        disabled: boolean;
    };
}

// --- Logic ---

const filterPortfolios = (portfolios: Portfolio[]): Portfolio[] => portfolios.filter((item) => item.PortfolioTypeId === portfolioTypeIds.MAIN);

export const adaptPortfolios = ({
    data,
    dfsId,
    portfolioActions,
    t,
    getFormattedCurrency,
}: AdaptPortfoliosParams): AdaptedPortfolio[] => {
    if (!data.Portfolios) return [];

    const portfolios = filterPortfolios(data.Portfolios);

    // Sort by date descending (Newest first)
    const sortedPortfolios = [...portfolios].sort((a, b) => new Date(b.CreateDate as string).getTime() - new Date(a.CreateDate as string).getTime());

    return sortedPortfolios.map((item): AdaptedPortfolio => {
        const portfolioStatus = getPortfolioStatus({
            portfolioStatusId: item.PortfolioStatusId,
        });

        const getLink = (): string => {
            if (portfolioStatus === NOT_IMPLEMENTED) {
                return `/client/${dfsId}/portfolios/${item.Id}/funding-information`;
            }

            return `/client/${dfsId}/portfolios/${item.Id}/tab/overview`;
        };

        return {
            UId: item.UId as string,
            Name: {
                label: item.Name || `${t('clientDashboard.portfolios.plan')} ${item.Id}`,
                link: getLink(),
                disabled: portfolioStatus === NOT_IMPLEMENTED,
            },
            Product: getfromDataObject(item, 'Product.Name') as string,
            Profile: validateStringData(item.RiskCategory.Name),
            Status: {
                text: (portfolioStatuses(t) as Record<string, string>)[portfolioStatus] || '-',
                value: portfolioStatus,
                color: (portfolioStatusesColors as Record<string, string>)[portfolioStatus] || '',
            },
            ExternalId: item.ExternalId as string,
            Value: getFormattedCurrency(item.CurrentValue as number, { currency: item.Currency.CurrencyCode }),
            Performance: item.CurrentPerformance ? item.CurrentPerformance * 100 : undefined,
            Actions: {
                value: 'action',
                actions: portfolioActions({
                    dfsId,
                    id: item.Id,
                    productName: getfromDataObject(item, 'Product.Name'),
                    t,
                    showEditButton: portfolioStatus !== NOT_IMPLEMENTED,
                    withEditActions: ![BLOCKED, PENDING_ORDERS]?.includes(portfolioStatus),
                    withPendingOrders: portfolioStatus === IN_EXECUTION,
                    withFundingInformation: portfolioStatus === NOT_IMPLEMENTED,
                }),
                disabled: portfolioStatus === CLOSED,
            },
        };
    });
};
